import React, { useState, useEffect } from "react";
import { Plus, Edit2, Trash2, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { useToast } from "@/components/ui/use-toast";
import { 
  fetchCommunityCategories, 
  createPostCategory, 
  updatePostCategory, 
  deletePostCategory 
} from "@/services/postCategoryService";
import { PostCategory } from "@/types/community";

interface CommunityCategories {
  communityId: string;
}

const predefinedColors = [
  "#00B6F0", "#1AD3F9", "#FF6B6B", "#4ECDC4", "#45B7D1",
  "#96CEB4", "#FFEAA7", "#DDA0DD", "#98D8C8", "#F7DC6F",
  "#BB8FCE", "#85C1E9", "#F8C471", "#82E0AA", "#F1948A"
];

export const CommunityCategories: React.FC<CommunityCategories> = ({ communityId }) => {
  const [categories, setCategories] = useState<PostCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState<PostCategory | null>(null);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  // Estados do formulário
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#00B6F0'
  });

  const { toast } = useToast();

  useEffect(() => {
    loadCategories();
  }, [communityId]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const data = await fetchCommunityCategories(communityId);
      setCategories(data);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
      toast({
        title: "Erro",
        description: "Falha ao carregar categorias",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      color: '#00B6F0'
    });
    setEditingCategory(null);
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast({
        title: "Erro",
        description: "Nome da categoria é obrigatório",
        variant: "destructive",
      });
      return;
    }

    try {
      setCreating(true);
      await createPostCategory({
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        community_id: communityId,
        color: formData.color
      });

      toast({
        title: "Sucesso!",
        description: "Categoria criada com sucesso",
      });

      setShowCreateDialog(false);
      resetForm();
      loadCategories();

    } catch (error: any) {
      console.error('Erro ao criar categoria:', error);
      toast({
        title: "Erro",
        description: error.message || "Falha ao criar categoria",
        variant: "destructive",
      });
    } finally {
      setCreating(false);
    }
  };

  const handleEdit = (category: PostCategory) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      color: category.color || '#00B6F0'
    });
  };

  const handleUpdate = async () => {
    if (!editingCategory || !formData.name.trim()) {
      toast({
        title: "Erro",
        description: "Nome da categoria é obrigatório",
        variant: "destructive",
      });
      return;
    }

    try {
      setUpdating(true);
      await updatePostCategory(editingCategory.id, {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        color: formData.color
      });

      toast({
        title: "Sucesso!",
        description: "Categoria atualizada com sucesso",
      });

      resetForm();
      loadCategories();

    } catch (error: any) {
      console.error('Erro ao atualizar categoria:', error);
      toast({
        title: "Erro",
        description: error.message || "Falha ao atualizar categoria",
        variant: "destructive",
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async (categoryId: string) => {
    try {
      setDeleting(categoryId);
      await deletePostCategory(categoryId);

      toast({
        title: "Sucesso!",
        description: "Categoria deletada com sucesso",
      });

      loadCategories();

    } catch (error: any) {
      console.error('Erro ao deletar categoria:', error);
      toast({
        title: "Erro",
        description: error.message || "Falha ao deletar categoria",
        variant: "destructive",
      });
    } finally {
      setDeleting(null);
    }
  };

  const CategoryForm = ({ isEdit = false }: { isEdit?: boolean }) => (
    <div className="space-y-4">
      <div>
        <label className="text-sm font-medium text-white mb-2 block">
          Nome da categoria *
        </label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          placeholder="Ex: Dicas, Builds, Replays..."
          className="bg-[#0F1419] border-[#222A35] text-white"
          maxLength={50}
        />
      </div>

      <div>
        <label className="text-sm font-medium text-white mb-2 block">
          Descrição (opcional)
        </label>
        <Textarea
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Descrição da categoria..."
          className="bg-[#0F1419] border-[#222A35] text-white resize-none"
          rows={3}
          maxLength={200}
        />
      </div>

      <div>
        <label className="text-sm font-medium text-white mb-2 block">
          Cor da categoria
        </label>
        <div className="flex flex-wrap gap-2">
          {predefinedColors.map((color) => (
            <button
              key={color}
              onClick={() => setFormData(prev => ({ ...prev, color }))}
              className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${
                formData.color === color 
                  ? 'border-white scale-110' 
                  : 'border-gray-500'
              }`}
              style={{ backgroundColor: color }}
              type="button"
            />
          ))}
        </div>
        <div className="mt-2 flex items-center gap-2">
          <span className="text-sm text-gray-400">Cor personalizada:</span>
          <input
            type="color"
            value={formData.color}
            onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
            className="w-8 h-8 rounded border border-gray-500 bg-transparent cursor-pointer"
          />
        </div>
      </div>

      <div className="flex gap-2 pt-4">
        <Button
          onClick={isEdit ? handleUpdate : handleCreate}
          disabled={isEdit ? updating : creating}
          className="bg-[#00B6F0] text-black hover:bg-[#1AD3F9] flex-1"
        >
          {(isEdit ? updating : creating) ? (
            <>
              <LoadingSpinner size="sm" className="mr-2" />
              {isEdit ? 'Atualizando...' : 'Criando...'}
            </>
          ) : (
            <>
              <Tag className="h-4 w-4 mr-2" />
              {isEdit ? 'Atualizar' : 'Criar'} Categoria
            </>
          )}
        </Button>
        <Button
          onClick={resetForm}
          variant="outline"
          className="border-[#222A35] text-white hover:bg-[#0F1419]"
        >
          Cancelar
        </Button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <Card className="bg-[#1A2333] border-[#222A35]">
        <CardContent className="py-8">
          <div className="text-center">
            <LoadingSpinner size="lg" />
            <p className="text-gray-400 mt-4">Carregando categorias...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-[#1A2333] border-[#222A35]">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2">
            <Tag className="h-5 w-5" />
            Categorias de Posts ({categories.length})
          </CardTitle>
          
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="bg-[#00B6F0] text-black hover:bg-[#1AD3F9]">
                <Plus className="h-4 w-4 mr-2" />
                Nova Categoria
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#1A2333] border-[#222A35] max-w-md">
              <DialogHeader>
                <DialogTitle className="text-white">Criar Nova Categoria</DialogTitle>
                <DialogDescription className="text-gray-400">
                  Crie categorias para organizar os posts da sua comunidade
                </DialogDescription>
              </DialogHeader>
              <CategoryForm />
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>

      <CardContent>
        {categories.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <Tag size={48} className="mx-auto mb-4 opacity-50" />
            <p>Nenhuma categoria criada ainda.</p>
            <p className="text-sm mt-2">
              Crie categorias para organizar os posts da sua comunidade!
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {categories.map((category) => (
              <div 
                key={category.id} 
                className="flex items-center justify-between p-4 bg-[#0F1419] rounded-lg border border-[#222A35]"
              >
                <div className="flex items-center gap-3">
                  <div 
                    className="w-4 h-4 rounded-full flex-shrink-0" 
                    style={{ backgroundColor: category.color || '#00B6F0' }}
                  />
                  <div>
                    <h4 className="text-white font-medium">{category.name}</h4>
                    {category.description && (
                      <p className="text-sm text-gray-400 mt-1">{category.description}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => handleEdit(category)}
                    variant="ghost"
                    size="sm"
                    className="text-gray-400 hover:text-white"
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-gray-400 hover:text-red-500"
                        disabled={deleting === category.id}
                      >
                        {deleting === category.id ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-[#1A2333] border-[#222A35]">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-white">
                          Deletar categoria
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-gray-400">
                          Tem certeza que deseja deletar a categoria "<strong>{category.name}</strong>"? 
                          Esta ação não pode ser desfeita e só é possível se não houver posts usando esta categoria.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="bg-transparent border-[#222A35] text-white hover:bg-[#0F1419]">
                          Cancelar
                        </AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDelete(category.id)}
                          className="bg-red-500 text-white hover:bg-red-600"
                        >
                          Deletar
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Dialog de edição */}
        {editingCategory && (
          <Dialog open={!!editingCategory} onOpenChange={() => resetForm()}>
            <DialogContent className="bg-[#1A2333] border-[#222A35] max-w-md">
              <DialogHeader>
                <DialogTitle className="text-white">Editar Categoria</DialogTitle>
                <DialogDescription className="text-gray-400">
                  Modifique as informações da categoria
                </DialogDescription>
              </DialogHeader>
              <CategoryForm isEdit />
            </DialogContent>
          </Dialog>
        )}
      </CardContent>
    </Card>
  );
}; 